/**
 * Organization-wide configuration types
 */
export const fhirProviders = ["Epic", "DEV_smart_demo_launcher"] as const;
export type FhirProvider = typeof fhirProviders[number];

export const mobileUnlockMethods = ["device", "pin"] as const;
export type MobileUnlockMethod = typeof mobileUnlockMethods[number];

export type FhirClientIdLookupResponse = {
  fhirAuthProvider: FhirProvider | "unknown",
  clientId: string | undefined,
}

export const barcodeTypes = [
  // 2d formats, cannot be confused for each other or anything else.
  'aztec', 'qr', 'pdf417', 'datamatrix',

  // ean and upc all have unique lengths compared to each other; the cannot be confused by a scanner
  // Most common in the US; _a is t he full 12 digit format, while _e is a smaller version
  'upc_a', 'upc_e',

   // Most common international barcode format. 8 is for smaller packages where 13 doesn't fit.'
  'ean13', 'ean8',

  'itf14', // Used for pallets, crates, and other bulk units
  'code128', // Another pallet / shipping standard, not used in retail or medical

  'code39', // Extremely legacy, unused in any modern application
  'code93', // Used only by Canada Post for delivery information. Never used for product tracking
  'codabar', // Last commercial usage ended in 2007
] as const

export type BarcodeType = typeof barcodeTypes[number]

export interface OrgConfig {
  /**
   * Whether mobile notifications are enabled for the organization
   */
  enableMobileNotifications: boolean;
  
  /**
   * Time window for mobile notifications in hours
   */
  mobileNotificationsWindow: number;

  /**
   * Whether location-based status updates like auto-arrive and auto-complete are enabled for the organization
   */
  enableLocationBasedStatusUpdates: boolean;

  /**
   * Idle session timeout in minutes before automatically logging out users (web only)
   */
  idleSessionTimeoutMinutes: number;

  /**
   * Idle timeout in minutes before locking the mobile app screen
   */
  mobileIdleLockTimeoutMinutes: number;

  mobileIdleSignOutMinutes: number;

  mobileUnlockMethod: MobileUnlockMethod;

  /**
   * How many minutes ahead of the current time should we not shuffle visits
   */
  shufflerLeadTime: number;

  /**
   * Ordered list of barcode formats to support in the mobile app.
   */
  barcodeTypes: BarcodeType[]
}

/**
 * Lead time used when an organization has not stored one, and the lowest choice in settings.
 * A stored value of 0 is left as 0.
 */
export const DEFAULT_SHUFFLER_LEAD_TIME_MINUTES = 5;

export const defaultOrgConfig: OrgConfig = {
  enableMobileNotifications: false,
  mobileNotificationsWindow: 24,
  enableLocationBasedStatusUpdates: false,
  idleSessionTimeoutMinutes: 15,
  mobileIdleLockTimeoutMinutes: 15,
  mobileIdleSignOutMinutes: 180,
  mobileUnlockMethod: "pin",
  shufflerLeadTime: DEFAULT_SHUFFLER_LEAD_TIME_MINUTES,
  barcodeTypes: ['aztec', 'qr', 'pdf417', 'datamatrix', 'upc_a', 'upc_e', 'ean13', 'ean8'],
};

/**
 * Merge user config with defaults
 */
export const getOrgConfigWithDefaults = (config?: Partial<OrgConfig> | null): OrgConfig => {
  return {
    ...defaultOrgConfig,
    ...config,
  };
};

/**
 * Whether an organization uses PIN unlock for mobile
 */
export const isPinUnlockUsable = (config: OrgConfig): boolean =>
  config.mobileUnlockMethod === "pin" &&
  config.mobileIdleLockTimeoutMinutes < config.mobileIdleSignOutMinutes;
